import React, { useState, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import HiddenCPQReport from "./HiddenCPQReport";
import Acumen_25_Years_LOGO from "../assets/Acumen_25_Years_LOGO.png";

// ─────────────────────────────────────────────────────────────────────────────
// STATIC LOOKUP TABLES
// These map the API's short keys → human-readable strings and copy text.
// ─────────────────────────────────────────────────────────────────────────────

// API key → display label shown in the UI
const DIMENSION_LABELS = {
  "Listening":    "Listening",
  "TPG":          "Talks with Purpose",
  "CBA":          "Clarity & Brevity",
  "N-Verbal":     "Non-Verbal Cues",
  "Inf.Share":    "Information Sharing",
  "Articulation": "Articulation",
};

// API interpretation string → CSS band class
const INTERP_TO_BAND = {
  "Excellent":    "good",
  "Very Good":         "good",
  "Suitable": "sat",
  "Nearly There":     "med",
  "Developing":   "low",
  "Needs improvement":         "low",
};

// Description shown inside Strongest Areas / Priority Gaps cards
const DIMENSION_DESCRIPTIONS = {
  "Listening":
    "Listening measures active attention and comprehension in conversations. A high score here reflects a strong ability to absorb and process spoken information.",
  "TPG":
    "Talks with Purpose reflects the ability to communicate with a clear goal in mind and bring intention into every conversation and meeting.",
  "CBA":
    "Clarity & Brevity covers the ability to express ideas clearly, briefly, and audibly. A low score here suggests inconsistency in message crispness and delivery.",
  "N-Verbal":
    "Non-Verbal Cues measures awareness and effective use of body language, eye contact, and facial expressions to reinforce communication.",
  "Inf.Share":
    "Information Sharing defines the ability to provide information quickly and accurately to internal and external stakeholders.",
  "Articulation":
    "Articulation measures syntax, phrase construction, idiom use, and grammar. A low score here is typically the clearest development need.",
};

// Intervention copy shown in the Priority Actions table (Page 4)
const DIMENSION_INTERVENTIONS = {
  "Listening": {
    intervention:  "Practice structured active-listening techniques: paraphrase what you hear before responding, and reduce filler responses.",
    expectedShift: "Improved comprehension, fewer misunderstandings, and stronger perceived empathy.",
  },
  "TPG": {
    intervention:  "Define a clear objective before every meeting or key conversation. Use a 3-point structure: purpose, message, close.",
    expectedShift: "More focused interactions and a reputation for productive, goal-driven communication.",
  },
  "CBA": {
    intervention:  "Adopt a pre-drafting habit before key meetings: opening point, single support point, then close — no more.",
    expectedShift: "Shorter, clearer, and more persuasive communication in both written and verbal settings.",
  },
  "N-Verbal": {
    intervention:  "Record and review yourself presenting; work with a coach on body-language alignment with your spoken words.",
    expectedShift: "Greater perceived confidence and improved listener engagement.",
  },
  "Inf.Share": {
    intervention:  "Build a consistent habit of structured knowledge-sharing — use brief daily or weekly summaries to keep stakeholders informed.",
    expectedShift: "Stronger trust and reduced information bottlenecks across teams.",
  },
  "Articulation": {
    intervention:  "Executive coaching on grammar precision, sentence shaping, verbal fluency, and structured oral delivery.",
    expectedShift: "Sharper executive presence and more polished spoken communication.",
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// HELPER: format a JS Date as "16 April 2026"
// ─────────────────────────────────────────────────────────────────────────────
function formatDate(date) {
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
export default function CPQResult() {
  const location = useLocation();
  const [isGenerating, setIsGenerating] = useState(false);
  const [pdfData, setPdfData] = useState(null);
  const generatingRef = useRef(false);

  // ── Trigger HiddenCPQReport PDF generation ─────────────────────────────
  const handleDownloadPDF = () => {
    if (generatingRef.current) return;   // synchronous guard — fires before any re-render
    generatingRef.current = true;
    setIsGenerating(true);
    setPdfData({ userInfo: { name, email, phone }, scores });
  };

  // Stable callback — avoids creating a new function reference on every render
  const handlePdfComplete = useCallback(() => {
    generatingRef.current = false;
    setIsGenerating(false);
    setPdfData(null);
  }, []);

  // ── Data received from the navigate() call ──────────────────────────────
  // state shape: { scores: <API data object>, name, email, phone }
  const { scores = {}, name = "—", email = "—", phone = "—" } =
    location.state || {};

  // ── Grand Total (Page 1 + Page 2) ───────────────────────────────────────
  const grandTotal     = scores["Grand_Total"] || { score: 0, out_of: 240, interpretation: "—" };
  const totalScore     = grandTotal.score;
  const maximumScore   = grandTotal.out_of;
  const interpretation = grandTotal.interpretation;

  // ── Page 1 cover values ─────────────────────────────────────────────────
  const participant    = name;
  const assessmentDate = formatDate(new Date());
  const overallResult  = `${totalScore} / ${maximumScore}`;

  // ── Pill / band CSS class derived from interpretation ───────────────────
  const bandClass = (interp) => INTERP_TO_BAND[interp] ?? "sat";

  // ── Score ring conic gradient (Page 2) ──────────────────────────────────
  const scorePct           = maximumScore > 0 ? (totalScore / maximumScore) * 100 : 0;
  const conicGradient      = `conic-gradient(var(--blue) 0 ${scorePct.toFixed(1)}%, #dfe7ee ${scorePct.toFixed(1)}% 100%)`;
  const indicatorFillWidth = `${scorePct.toFixed(1)}%`;
  const benchmarkPct       = (218 / maximumScore) * 100;
  const markerLeft         = `${benchmarkPct.toFixed(1)}%`;

  // ── Dimension rows for Page 3 bar chart ─────────────────────────────────
  // Preserve a consistent display order; skip Grand_Total
  const DISPLAY_ORDER = ["Inf.Share", "TPG", "Listening", "N-Verbal", "CBA", "Articulation"];

  const dimensions = DISPLAY_ORDER
    .filter((key) => scores[key])
    .map((key) => ({
      key,
      label:     DIMENSION_LABELS[key] || key,
      score:     scores[key].score,
      max:       scores[key].out_of,
      band:      bandClass(scores[key].interpretation),
      bandLabel: scores[key].interpretation,
    }));

  // ── Strongest Areas: top 2 dimensions by score ──────────────────────────
  const strongestAreas = [...dimensions]
    .sort((a, b) => b.score - a.score)
    .slice(0, 2)
    .map((d) => ({
      title: d.label,
      body:  DIMENSION_DESCRIPTIONS[d.key] || "",
    }));

  // ── Priority Gaps: bottom 2 dimensions by score ─────────────────────────
  const priorityGaps = [...dimensions]
    .sort((a, b) => a.score - b.score)
    .slice(0, 2)
    .map((d) => ({
      title: d.label,
      body:  DIMENSION_DESCRIPTIONS[d.key] || "",
    }));

  // ── Priority Actions: bottom 3 dimensions by score (Page 4 table) ───────
  const priorityActions = [...dimensions]
    .sort((a, b) => a.score - b.score)
    .slice(0, 3)
    .map((d) => ({
      focusArea:     d.label,
      currentScore:  `${d.score} / ${d.max}`,
      intervention:  DIMENSION_INTERVENTIONS[d.key]?.intervention  || "—",
      expectedShift: DIMENSION_INTERVENTIONS[d.key]?.expectedShift || "—",
    }));

  // ── Dynamic callout sentence for Page 2 ─────────────────────────────────
  const topDim    = strongestAreas[0]?.title || "—";
  const bottomDim = priorityGaps[0]?.title   || "—";

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="cpq-report" id="cpq-report-content">
        <link href="https://api.fontshare.com/v2/css?f[]=satoshi@300,400,500,700,900&display=swap" rel="stylesheet" />
        <style>{`
        .cpq-report {
          --text-xs: clamp(0.75rem, 0.7rem + 0.25vw, 0.875rem);
          --text-sm: clamp(0.875rem, 0.8rem + 0.35vw, 1rem);
          --text-base: clamp(1rem, 0.95rem + 0.25vw, 1.125rem);
          --text-lg: clamp(1.125rem, 1rem + 0.75vw, 1.4rem);
          --text-xl: clamp(1.45rem, 1.2rem + 1.1vw, 2rem);
          --space-1:.25rem; --space-2:.5rem; --space-3:.75rem; --space-4:1rem;
          --space-5:1.25rem; --space-6:1.5rem; --space-8:2rem; --space-10:2.5rem;
          --space-12:3rem; --space-16:4rem; --space-20:5rem;
          --navy:#0f2744; --blue:#c62828; --gold:#d6a23f; --ink:#1f2937; --muted:#6b7280;
          --bg:#f4f6f8; --card:#ffffff; --line:#d9e0e7; --soft:#eef2f6;
          --good:#1f8f55; --sat:#3f7cac; --med:#d88a1f; --low:#c2413b;
          --radius:20px; --radius-sm:12px; --shadow:0 18px 40px rgba(14,39,68,.10);
          --font-body:'Satoshi', sans-serif;

          /* Logo panel: fluid width clamped between 180px and 500px */
            --logo-w: clamp(180px, 42vw, 500px);
            --logo-h: clamp(90px,  21vw, 250px);
            --logo-img-h: clamp(30px, 6vw, 65px);
            --logo-pl: clamp(20px, 5vw, 60px);
        }
        .cpq-report *, .cpq-report *::before, .cpq-report *::after { box-sizing:border-box; }
        .cpq-report {
          font-family:var(--font-body);
          background:linear-gradient(180deg,#eef4f9 0%,#f7fafc 100%);
          color:var(--ink); line-height:1.5;
        }
        .cpq-report .report { max-width:1100px; margin:0 auto; padding:var(--space-8) var(--space-4) var(--space-16); }
        .cpq-report .page {
          background:var(--card); border:1px solid var(--line); border-radius:28px;
          box-shadow:var(--shadow); overflow:hidden; margin-bottom:var(--space-8);
          page-break-after:always;
        }
        .cpq-report .cover {
          min-height:92vh;
          position:relative;
          background:radial-gradient(circle at top right,rgba(214,162,63,.22),transparent 28%),
                     linear-gradient(135deg,#A81026,#C4163A 55%,#7B0F2B);
          color:white;
        }
        .cpq-report .cover-inner { padding:var(--space-16); display:flex; flex-direction:column; justify-content:space-between; min-height:92vh; }
        /* Logo panel — white diagonal shape top-right (matches VAK) */
        .cpq-report .logo-panel {
            position: absolute;
            top: 0;
            right: 0;
            width: var(--logo-w);
            height: var(--logo-h);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1;
            pointer-events: none;
          }
          .cpq-report .logo-panel-inner {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            position: relative;
            z-index: 2;
            padding-left: var(--logo-pl);
            padding-bottom: clamp(8px, 2vw, 25px);
          }
          .cpq-report .logo-img {
            height: var(--logo-img-h);
            width: auto;
            object-fit: contain;
            flex-shrink: 0;
          }
        /* AB fallback text logo inside the white panel */
        .cpq-report .logo-text {
          font-size:1.6rem; font-weight:900; letter-spacing:.1em;
          color:#A81026; line-height:1;
        }
        .cpq-report .logo-subtext {
          font-size:0.6rem; font-weight:700; letter-spacing:.14em;
          text-transform:uppercase; color:#7B0F2B; margin-top:4px; text-align:center;
        }
        .cpq-report .brand {
          display:flex; align-items:center; justify-content:space-between;
          gap:var(--space-4); position:relative; z-index:2;
          padding-right:clamp(0px,38vw,200px);
        }
        .cpq-report .badge { border:1px solid rgba(255,255,255,.18); background:rgba(255,255,255,.08); padding:clamp(.3rem,.8vw,.6rem) clamp(.5rem,1.2vw,1rem); border-radius:999px; font-size:clamp(0.58rem,1.5vw,0.85rem); white-space:nowrap; flex-shrink:0; }
        .cpq-report .cover-title { margin-top:var(--space-16); }
        .cpq-report .cover-title h2 { margin:0 0 var(--space-4); font-size:clamp(2.6rem,5vw,4.8rem); line-height:.96; max-width:9ch; }
        .cpq-report .cover-title p  { font-size:var(--text-lg); max-width:100%; color:rgba(255,255,255,.84); text-align:justify; }
        .cpq-report .cover-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:var(--space-4); margin-top:var(--space-12); }
        .cpq-report .meta { background:rgba(255,255,255,.08); border:1px solid rgba(255,255,255,.16); border-radius:18px; padding:1rem 1.1rem; font-size:1.2rem; }
        .cpq-report .meta span   { display:block; font-size:var(--text-xs); color:rgba(255,255,255,.66); margin-bottom:.25rem; }
        .cpq-report .meta strong { font-size:var(--text-base); color:white; }
        /* Cover badge pills row */
        .cpq-report .cover-badges { display:flex; flex-wrap:wrap; gap:.6rem; margin-top:var(--space-8); }
        .cpq-report .cover-badge  { background:rgba(255,255,255,.12); border:1px solid rgba(255,255,255,.18); padding:.5rem .9rem; border-radius:999px; font-size:0.95rem; }
        .cpq-report .footer-note { display:flex; justify-content:space-between; gap:var(--space-4); color:rgba(255,255,255,.72); font-size:var(--text-xs); margin-top:var(--space-10); }
        .cpq-report .page-inner { padding:var(--space-10); }
        .cpq-report .topbar { display:flex; justify-content:space-between; align-items:flex-start; gap:var(--space-4); margin-bottom:var(--space-8); }
        .cpq-report .section-kicker { color:var(--blue); font-size:var(--text-xs); text-transform:uppercase; letter-spacing:.12em; font-weight:700; }
        .cpq-report .section-title  { margin:.35rem 0 0; font-size:var(--text-xl); color:var(--navy); }
        .cpq-report .summary-grid   { display:grid; grid-template-columns:1.15fr .85fr; gap:var(--space-6); }
        .cpq-report .two-col        { display:grid; grid-template-columns:repeat(2,1fr); gap:var(--space-6); margin-top:var(--space-6); }
        .cpq-report .card { background:linear-gradient(180deg,#fff 0%,#fbfcfd 100%); border:1px solid var(--line); border-radius:var(--radius); padding:var(--space-6); }
        .cpq-report .card h3 { margin:0 0 .75rem; font-size:var(--text-lg); color:var(--navy); }
        .cpq-report .lead { color:#334155; font-size:var(--text-base); text-align:justify; }
        .cpq-report .score-wrap { display:grid; place-items:center; gap:.75rem; }
        .cpq-report .score-ring { width:240px; aspect-ratio:1; border-radius:50%; display:grid; place-items:center; position:relative; }
        .cpq-report .score-ring::before { content:""; position:absolute; inset:18px; background:white; border-radius:50%; box-shadow:inset 0 0 0 1px var(--line); }
        .cpq-report .score-core { position:relative; text-align:center; z-index:2; }
        .cpq-report .score-core strong { display:block; font-size:3rem; color:var(--navy); line-height:1; }
        .cpq-report .score-core span   { display:block; color:var(--muted); font-size:var(--text-sm); }
        .cpq-report .pill { display:inline-flex; align-items:center; gap:.45rem; padding:.5rem .85rem; border-radius:999px; font-size:var(--text-xs); font-weight:700; text-transform:uppercase; letter-spacing:.06em; }
        .cpq-report .pill.sat  { background:#fde8e8; color:var(--blue); }
        .cpq-report .pill.good { background:#fef2f2; color:var(--good); }
        .cpq-report .pill.med  { background:#fff1f2; color:var(--med); }
        .cpq-report .pill.low  { background:#fee2e2; color:var(--low); }
        .cpq-report .mini-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:var(--space-4); margin-top:var(--space-6); }
        .cpq-report .mini { background:var(--soft); border-radius:16px; padding:1rem; }
        .cpq-report .mini span   { display:block; font-size:var(--text-xs); color:var(--muted); margin-bottom:.25rem; }
        .cpq-report .mini strong { font-size:1.45rem; color:var(--navy); }
        .cpq-report .visual-card { background:var(--navy); color:white; border-radius:24px; padding:var(--space-6); }
        .cpq-report .visual-card h3 { color:white; }
        .cpq-report .band-scale { display:grid; grid-template-columns:repeat(6,1fr); gap:.5rem; margin:1.1rem 0 1rem; }
        .cpq-report .band { border-radius:14px; padding:.75rem .6rem; min-height:88px; display:flex; flex-direction:column; justify-content:space-between; font-size:var(--text-xs); }
        .cpq-report .b1{background:#5b1f24;} .b2{background:#8b4b28;} .b3{background:#a16f22;}
        .cpq-report .b4{background:#375b7a;} .b5{background:#1c6b48;} .b6{background:#0f8b59;}
        .cpq-report .band strong { font-size:var(--text-sm); color:white; }
        .cpq-report .band em     { font-style:normal; color:rgba(255,255,255,.8); }
        .cpq-report .indicator { margin-top:1rem; background:rgba(255,255,255,.07); border-radius:16px; padding:1rem; border:1px solid rgba(255,255,255,.08); }
        .cpq-report .indicator-track { height:16px; background:rgba(255,255,255,.12); border-radius:999px; overflow:hidden; position:relative; }
        .cpq-report .indicator-fill   { height:100%; background:linear-gradient(90deg,#6fb2ff 0%,#b9d8ff 100%); border-radius:999px; }
        .cpq-report .indicator-marker { position:absolute; top:-5px; width:3px; height:26px; background:var(--gold); border-radius:99px; }
        .cpq-report .ind-row { display:flex; justify-content:space-between; font-size:var(--text-xs); color:rgba(255,255,255,.72); margin-top:.6rem; }
        .cpq-report .bars    { display:grid; gap:0.5rem; }
        .cpq-report .bar-row { display:grid; grid-template-columns:180px 1fr 110px; gap:1rem; align-items:center; padding:0.5rem 0.75rem; border-radius:12px; transition:background 0.15s ease; }
        .cpq-report .bar-row:hover { background:#f8fafc; }
        .cpq-report .bar-row.row-good { border-left:3px solid var(--good); }
        .cpq-report .bar-row.row-sat  { border-left:3px solid var(--sat);  }
        .cpq-report .bar-row.row-med  { border-left:3px solid var(--med);  }
        .cpq-report .bar-row.row-low  { border-left:3px solid var(--low);  }
        .cpq-report .bar-label { font-size:var(--text-sm); font-weight:700; color:var(--navy); }
        .cpq-report .track { height:16px; background:#e9eef3; border-radius:999px; overflow:hidden; }
        .cpq-report .fill { height:100%; border-radius:999px; transition:width 0.6s ease; }
        .cpq-report .fill.good { background:linear-gradient(90deg,#1f8f55,#56c985); }
        .cpq-report .fill.sat  { background:linear-gradient(90deg,#3f7cac,#74a7d5); }
        .cpq-report .fill.med  { background:linear-gradient(90deg,#d88a1f,#f0b256); }
        .cpq-report .fill.low  { background:linear-gradient(90deg,#c2413b,#ea7a71); }
        .cpq-report .score-box { text-align:right; font-variant-numeric:tabular-nums; }
        .cpq-report .score-box strong { display:block; color:var(--navy); }
        .cpq-report .score-box span   { font-size:var(--text-xs); font-weight:700; }
        .cpq-report .score-box span.band-good { color:var(--good); }
        .cpq-report .score-box span.band-sat  { color:var(--sat);  }
        .cpq-report .score-box span.band-med  { color:var(--med);  }
        .cpq-report .score-box span.band-low  { color:var(--low);  }
        /* 6 unique dimension colors for Visual Score Dashboard */
        .cpq-report .fill.d-listening    { background:linear-gradient(90deg,#1e3a8a,#3b82f6); }
        .cpq-report .fill.d-tpg          { background:linear-gradient(90deg,#7f1d1d,#ef4444); }
        .cpq-report .fill.d-cba          { background:linear-gradient(90deg,#b45309,#f59e0b); }
        .cpq-report .fill.d-nverbal      { background:linear-gradient(90deg,#064e3b,#10b981); }
        .cpq-report .fill.d-infshare     { background:linear-gradient(90deg,#4c1d95,#8b5cf6); }
        .cpq-report .fill.d-articulation { background:linear-gradient(90deg,#9d174d,#ec4899); }
        .cpq-report .bar-row.row-d-listening    { border-left:3px solid #3b82f6; }
        .cpq-report .bar-row.row-d-tpg          { border-left:3px solid #ef4444; }
        .cpq-report .bar-row.row-d-cba          { border-left:3px solid #f59e0b; }
        .cpq-report .bar-row.row-d-nverbal      { border-left:3px solid #10b981; }
        .cpq-report .bar-row.row-d-infshare     { border-left:3px solid #8b5cf6; }
        .cpq-report .bar-row.row-d-articulation { border-left:3px solid #ec4899; }
        .cpq-report .score-box span.band-d-listening    { color:#1e3a8a; }
        .cpq-report .score-box span.band-d-tpg          { color:#7f1d1d; }
        .cpq-report .score-box span.band-d-cba          { color:#b45309; }
        .cpq-report .score-box span.band-d-nverbal      { color:#064e3b; }
        .cpq-report .score-box span.band-d-infshare     { color:#4c1d95; }
        .cpq-report .score-box span.band-d-articulation { color:#9d174d; }
        .cpq-report .list { display:grid; gap:.9rem; }
        .cpq-report .item { border:1px solid var(--line); border-radius:16px; padding:1rem; background:#fff; }
        .cpq-report .item strong { display:block; color:var(--navy); margin-bottom:.25rem; }
        .cpq-report .item p { margin:0; color:#475569; font-size:var(--text-sm); text-align:justify; }
        .cpq-report table { width:100%; border-collapse:collapse; overflow:hidden; border-radius:16px; }
        .cpq-report th, .cpq-report td { padding:.95rem 1rem; border-bottom:1px solid var(--line); vertical-align:top; }
        .cpq-report td { text-align:justify; }
        .cpq-report th { text-align:left; }
        .cpq-report th { background:#eff4f8; color:var(--navy); font-size:var(--text-xs); text-transform:uppercase; letter-spacing:.08em; }
        .cpq-report tr:last-child td { border-bottom:none; }
        .cpq-report .callout { background:linear-gradient(135deg,#fff7f7,#fbeaea); border:1px solid var(--line); border-radius:18px; padding:1rem 1.1rem; color:#334155; text-align:justify; }
        .cpq-report .back-cover { color:white; }
        .cpq-report .back-cover-gradient { background:radial-gradient(circle at top right,rgba(214,162,63,.22),transparent 28%), linear-gradient(135deg,#A81026,#C4163A 55%,#7B0F2B); min-height:56vh; }
        .cpq-report .back-cover-header { display:flex; flex-wrap:wrap; justify-content:space-between; align-items:center; gap:0.5rem; padding:clamp(0.9rem,2.5vw,1.5rem) clamp(0.9rem,3vw,2.5rem); border-bottom:1px solid rgba(255,255,255,0.15); }
        .cpq-report .back-cover .page-inner { display:flex; flex-direction:column; justify-content:space-between; }
        .cpq-report .closing h2 { margin:0 0 1rem; font-size:clamp(1.4rem,4vw,3.25rem); color:#FFFFFF; }
        .cpq-report .closing p  { max-width:100%; color:rgba(255,255,255,.84); font-size:clamp(0.85rem,2vw,1.2rem); text-align:justify; }
        .cpq-report .contact-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(min(140px,100%),1fr)); gap:clamp(0.4rem,1.2vw,1rem); margin-top:var(--space-8); }
        .cpq-report .contact { background:rgba(168,16,38,.1); border:1px solid rgba(255,255,255,.16); border-radius:clamp(10px,2vw,16px); padding:clamp(0.6rem,1.5vw,.9rem); min-width:0; }
        .cpq-report .contact span   { display:block; color:rgba(255,255,255,.64); font-size:var(--text-xs); margin-bottom:.3rem; }
        .cpq-report .contact strong { font-size:clamp(0.78rem,1.6vw,0.92rem); word-break:break-word; }
        .cpq-report .small { font-size:var(--text-xs); color:var(--muted); }
        .cpq-report .light { color:rgba(255,255,255,.68); }
        @media print {
          .cpq-report { background:white; }
          .cpq-report .report { max-width:none; padding:0; }
          .cpq-report .page { box-shadow:none; border-radius:0; margin:0 0 12px; border:1px solid #ddd; }
        }
        /* ── Responsive: Tablet (≤ 768px) ── */
        @media (max-width:768px) {
          .cpq-report .report { padding:1.25rem 0.75rem 3rem; }
          .cpq-report .page { border-radius:20px; margin-bottom:1.25rem; }
          

          
          .cpq-report .cover { min-height:88vh; }
          .cpq-report .cover-inner { padding:2.25rem; min-height:88vh; }
          .cpq-report .cover-title { margin-top:2.5rem; }
          .cpq-report .cover-title h2 { font-size:clamp(2rem,5vw,3.2rem); }
          .cpq-report .cover-grid { gap:0.75rem; margin-top:1.5rem; grid-template-columns:repeat(auto-fit,minmax(min(130px,100%),1fr)); }
          .cpq-report .page-inner { padding:1.75rem; }
          .cpq-report .topbar { flex-wrap:wrap; gap:0.5rem; }
          .cpq-report .pill { font-size:10px; padding:.35rem .6rem; }
          .cpq-report .summary-grid, .cpq-report .two-col { grid-template-columns:1fr; }
          .cpq-report .band-scale { grid-template-columns:repeat(3,1fr); }
          .cpq-report .score-ring { width:200px; }
          .cpq-report .bar-row { grid-template-columns:90px 1fr auto; gap:.5rem; }
          .cpq-report .mini-grid { grid-template-columns:repeat(3,1fr); }
        }
        /* ── Responsive: Mobile (≤ 480px) ── */
        @media (max-width:480px) {
          .cpq-report .report { padding:0.75rem 0.5rem 2.5rem; }
          .cpq-report .page { border-radius:16px; margin-bottom:0.9rem; }
          
          
          
          .cpq-report .cover { min-height:100svh; }
          .cpq-report .cover-inner { padding:1.25rem; min-height:100svh; }
          .cpq-report .cover-title { margin-top:1.5rem; }
          .cpq-report .cover-title h2 { font-size:clamp(1.6rem,7vw,2.2rem); line-height:1.08; }
          .cpq-report .cover-title p  { font-size:0.875rem; }
          .cpq-report .cover-grid { grid-template-columns:repeat(auto-fit,minmax(min(130px,100%),1fr)); gap:0.5rem; margin-top:1.1rem; }
          .cpq-report .meta { padding:0.6rem 0.75rem; }
          .cpq-report .cover-badges { margin-top:0.9rem; gap:0.4rem; }
          .cpq-report .cover-badge { font-size:0.7rem; padding:0.3rem 0.55rem; }
          .cpq-report .footer-note { flex-direction:column; gap:0.2rem; font-size:0.68rem; margin-top:1rem; }
          .cpq-report .page-inner { padding:1rem; }
          .cpq-report .topbar { flex-wrap:wrap; gap:0.4rem; margin-bottom:1rem; }
          .cpq-report .section-title { font-size:1.25rem; }
          .cpq-report .pill { font-size:9px; padding:.25rem .5rem; }
          .cpq-report .card { padding:0.875rem; border-radius:14px; }
          .cpq-report .card h3 { font-size:0.95rem; margin-bottom:0.45rem; }
          .cpq-report .lead { font-size:0.84rem; }
          /* bar-row: label | track | score/band stacked — score-box uses auto so it never overflows */
          .cpq-report .bar-row { grid-template-columns:68px 1fr auto; gap:0.4rem; }
          .cpq-report .bar-label { font-size:0.77rem; }
          .cpq-report .track { height:18px; }
          .cpq-report .score-box { min-width:0; }
          .cpq-report .score-box strong { font-size:0.8rem; }
          .cpq-report .score-box span { font-size:0.68rem; }
          .cpq-report .mini-grid { grid-template-columns:1fr; }
          .cpq-report .band-scale { grid-template-columns:repeat(2,1fr); }
          .cpq-report .score-ring { width:170px; }
          .cpq-report .contact-grid { grid-template-columns:1fr; gap:0.5rem; margin-top:1rem; }
          .cpq-report .closing h2 { font-size:clamp(1.45rem,5.5vw,2rem); }
          .cpq-report .closing p  { font-size:0.88rem; }
          /* Priority table: stack columns on mobile */
          .cpq-report table, .cpq-report thead, .cpq-report tbody,
          .cpq-report th, .cpq-report td, .cpq-report tr { display:block; }
          .cpq-report thead tr { position:absolute; top:-9999px; left:-9999px; }
          .cpq-report tr { border:1px solid var(--line); border-radius:12px; margin-bottom:0.75rem; padding:0.5rem; }
          .cpq-report td { border:none; border-bottom:1px solid #f0f4f8; padding:0.5rem 0.6rem; font-size:0.82rem; }
          .cpq-report td:before { content:attr(data-label); display:block; font-weight:700; color:var(--navy); font-size:0.7rem; text-transform:uppercase; letter-spacing:.06em; margin-bottom:0.2rem; }
          .cpq-report td:last-child { border-bottom:none; }
        }
        /* ── Responsive: Tiny (≤ 390px) — covers 360×640 and similar ── */
        @media (max-width:390px) {
          .cpq-report .report { padding:0.5rem 0.35rem 2rem; }
          .cpq-report .page { border-radius:12px; margin-bottom:0.75rem; }
          
          
          
          .cpq-report .cover { min-height:100svh; }
          .cpq-report .cover-inner { padding:1rem; min-height:100svh; }
          .cpq-report .brand { padding-right:clamp(0px,44vw,160px); }
          .cpq-report .badge { font-size:0.55rem; padding:0.25rem 0.5rem; }
          .cpq-report .cover-title { margin-top:1rem; }
          .cpq-report .cover-title h2 { font-size:clamp(1.35rem,6.5vw,1.7rem); line-height:1.1; max-width:100%; }
          .cpq-report .cover-title p  { font-size:0.78rem; max-width:100%; }
          .cpq-report .cover-grid { grid-template-columns:1fr; gap:0.4rem; margin-top:0.9rem; }
          .cpq-report .meta { padding:0.5rem 0.65rem; font-size:1rem; border-radius:12px; }
          .cpq-report .meta strong { font-size:0.82rem; }
          .cpq-report .cover-badges { margin-top:0.75rem; gap:0.3rem; }
          .cpq-report .cover-badge { font-size:0.65rem; padding:0.25rem 0.45rem; }
          .cpq-report .footer-note { flex-direction:column; gap:0.15rem; font-size:0.62rem; margin-top:0.75rem; }
          .cpq-report .page-inner { padding:0.85rem; }
          .cpq-report .bar-row { grid-template-columns:54px 1fr auto; gap:0.3rem; }
          .cpq-report .bar-label { font-size:0.72rem; }
          .cpq-report .track { height:16px; }
          .cpq-report .score-box strong { font-size:0.72rem; }
          .cpq-report .score-box span { font-size:0.62rem; }
          .cpq-report .score-ring { width:150px; }
          .cpq-report .mini-grid { grid-template-columns:1fr; gap:0.4rem; }
          .cpq-report .band-scale { grid-template-columns:repeat(2,1fr); gap:0.3rem; }
          .cpq-report .band { min-height:72px; padding:0.55rem 0.45rem; }
          .cpq-report .contact-grid { grid-template-columns:1fr; gap:0.4rem; margin-top:0.75rem; }
        }
        /* ── Download Button ── */
        #cpq-pdf-btn {
          position: fixed;
          bottom: 2rem;
          right: 2rem;
          z-index: 9999;
          display: inline-flex;
          align-items: center;
          gap: 0.6rem;
          padding: 0.85rem 1.6rem;
          background: linear-gradient(135deg, #d6a23f, #c8891a);
          color: white;
          font-family: var(--font-body);
          font-size: 0.9rem;
          font-weight: 700;
          letter-spacing: 0.06em;
          border: none;
          border-radius: 999px;
          box-shadow: 0 8px 28px rgba(214,162,63,0.45), 0 2px 8px rgba(0,0,0,0.18);
          cursor: pointer;
          transition: transform 0.18s ease, box-shadow 0.18s ease, opacity 0.18s ease;
          text-transform: uppercase;
        }
        #cpq-pdf-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 14px 36px rgba(214,162,63,0.55), 0 2px 8px rgba(0,0,0,0.18);
        }
        #cpq-pdf-btn:active:not(:disabled) { transform: translateY(0); }
        #cpq-pdf-btn:disabled { opacity: 0.72; cursor: not-allowed; }
        #cpq-pdf-btn .btn-spinner {
          width: 16px; height: 16px;
          border: 2px solid rgba(255,255,255,0.35);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.7s linear infinite;
          flex-shrink: 0;
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        @media print { #cpq-pdf-btn { display: none !important; } }
      `}</style>

      {/* ── Floating PDF Download Button ── */}
      <button
        id="cpq-pdf-btn"
        onClick={handleDownloadPDF}
        disabled={isGenerating}
        aria-label="Download PDF Report"
      >
        {isGenerating ? (
          <>
            <span className="btn-spinner" />
            Generating PDF…
          </>
        ) : (
          <>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            Download PDF
          </>
        )}
      </button>

      <div className="report">

        {/* ══════════════════════════════════════════
            PAGE 1 — COVER
            Data source: location.state → { name, scores.Grand_Total }
        ══════════════════════════════════════════ */}
        <section className="page cover">
          <div className="cover-inner">

            {/* ── White diagonal logo panel — top-right (matches VAK) ── */}
            <div className="logo-panel">
                <svg
                  width="100%" height="100%"
                  viewBox="0 0 500 250"
                  style={{ position: 'absolute', top: 0, right: 0, zIndex: 0 }}
                  preserveAspectRatio="none"
                >
                  <path d="M 60,8 L 500,8 L 500,188 L 150,253 Q 120,258 113,229 Z" fill="rgba(0,0,0,0.04)" />
                  <path d="M 60,4 L 500,4 L 500,184 L 150,249 Q 120,254 113,225 Z" fill="rgba(0,0,0,0.06)" />
                  <path d="M 60,0 L 500,0 L 500,180 L 150,245 Q 120,250 113,221 Z" fill="white" />
                </svg>
                <div className="logo-panel-inner">
                  <img src={Acumen_25_Years_LOGO} alt="Acumen Logo" className="logo-img" />
                </div>
              </div>

              <div className="brand">
                <div className="badge">Confidential Individual Report</div>
              </div>

            {/* ── Main cover content — centred vertically ── */}
            <div style={{ display:"flex", flexDirection:"column", justifyContent:"center", flex:1 }}>
              <div className="cover-title">
                <h2>Communication Proficiency Assessment</h2>
                <p>
                  This report presents a comprehensive analysis of your communication
                  strengths and development areas based on the six dimensions of the
                  Communication Proficiency Questionnaire — delivered in a consulting-ready
                  visual format.
                </p>
              </div>

              <div className="cover-grid">
                <div className="meta">
                  <span>Participant</span>
                  <strong>{participant}</strong>
                </div>
                <div className="meta">
                  <span>Assessment Date</span>
                  <strong>{assessmentDate}</strong>
                </div>
                <div className="meta">
                  <span>Overall Result</span>
                  <strong>{overallResult}</strong>
                </div>
              </div>

              <div className="cover-badges">
                {["Executive Summary","Dimension Profile","Development Plan"].map((b) => (
                  <span key={b} className="cover-badge">{b}</span>
                ))}
              </div>
            </div>

            <div className="footer-note">
              <div>Based on the six communication dimensions defined in the Communication Proficiency Questionnaire.</div>
              {/* <div>Excellent starts at 218+.</div> */}
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════════
            PAGE 2 — EXECUTIVE SUMMARY
            Data source: scores.Grand_Total
        ══════════════════════════════════════════ */}
        <section className="page">
          <div className="page-inner">
            <div className="topbar">
              <div>
                <div className="section-kicker">Page 2</div>
                <h2 className="section-title">Executive Summary</h2>
              </div>
              <div className={`pill ${bandClass(interpretation)}`}>
                Overall band: {interpretation}
              </div>
            </div>

            <div className="summary-grid">
              
              <div className="card">
                <h3>Assessment Purpose</h3>
                <p className="lead">
                  This report is designed to identify clear communication strengths
                  and priority development areas after an individual completes the
                  Communication Proficiency Questionnaire. The score interpretation
                  follows the defined total score bands: Needs improvement (148 and below),
                  Developing (149–166), Nearly There(167–184), Suitable (185–201),
                  Very Good (202–217), and Excellent (218+).
                </p>

                <div className="mini-grid">
                  <div className="mini">
                    <span>Total score</span>
                    <strong>{totalScore}</strong>
                  </div>
                  <div className="mini">
                    <span>Maximum score</span>
                    <strong>{maximumScore}</strong>
                  </div>
                  <div className="mini">
                    <span>Interpretation</span>
                    <strong>{interpretation}</strong>
                  </div>
                </div>

               
                <div className="callout" style={{ marginTop: "var(--space-6)" }}>
                  <strong>{participant}</strong> presents a communication profile with
                  a standout strength in <strong>{topDim}</strong> and a priority
                  development area in <strong>{bottomDim}</strong>. The overall score
                  of <strong>{totalScore}</strong> places this participant in
                  the <strong>{interpretation}</strong> band, pointing to clear,
                  actionable areas for growth.
                </div>
              </div>

              <div className="visual-card">
                <h3>Overall Score</h3>
                <div className="score-wrap">
                  <div
                    className="score-ring"
                    style={{ background: conicGradient }}
                  >
                    <div className="score-core">
                      <strong>{totalScore}</strong>
                      <span>out of {maximumScore}</span>
                    </div>
                  </div>
                  <div className={`pill ${bandClass(interpretation)}`}>
                    {interpretation}
                  </div>
                </div>

                <div className="indicator">
                  <div className="indicator-track" style={{ position: "relative" }}>
                    <div
                      className="indicator-fill"
                      style={{ width: indicatorFillWidth }}
                    />
                    <div
                      className="indicator-marker"
                      style={{ left: markerLeft, position: "absolute" }}
                    />
                  </div>
                  <div className="ind-row">
                    <span>Current score</span>
                    <span>Benchmark for Good / Excellent</span>
                  </div>
                  <div className="ind-row">
                    <strong style={{ color: "white" }}>{totalScore}</strong>
                    <strong style={{ color: "var(--gold)" }}>218+</strong>
                  </div>
                </div>

                <div className="band-scale">
                  <div className="band b1"><strong>Needs improvement</strong><em>148 &amp; below</em></div>
                  <div className="band b2"><strong>Developing</strong><em>149–166</em></div>
                  <div className="band b3"><strong>Nearly There</strong><em>167–184</em></div>
                  <div className="band b4"><strong>Suitable</strong><em>185–201</em></div>
                  <div className="band b5"><strong>Very Good</strong><em>202–217</em></div>
                  <div className="band b6"><strong>Excellent</strong><em>218+</em></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════════
            PAGE 3 — DIMENSION PROFILE
            Data source: scores (all keys except Grand_Total)
        ══════════════════════════════════════════ */}
        <section className="page">
          <div className="page-inner">
            <div className="topbar">
              <div>
                <div className="section-kicker">Page 3</div>
                <h2 className="section-title">Dimension Profile</h2>
              </div>
              <div className="small">Max 40 per dimension</div>
            </div>

            {/* Bar chart — rendered from dimensions[] derived from API scores */}
            <div className="card" style={{ marginBottom: "var(--space-6)" }}>
              <h3>Visual Score Dashboard</h3>
              <div className="bars">
                {dimensions.map((dim) => {
                  const dimKey = dim.key.toLowerCase().replace(/[^a-z]/g, "");
                  const colorKey =
                    dimKey === "listening"    ? "d-listening"    :
                    dimKey === "tpg"          ? "d-tpg"          :
                    dimKey === "cba"          ? "d-cba"          :
                    dimKey === "nverbal"      ? "d-nverbal"      :
                    dimKey === "infshare"     ? "d-infshare"     :
                    dimKey === "articulation" ? "d-articulation" : dim.band;
                  return (
                    <div className={`bar-row row-${colorKey}`} key={dim.key}>
                      <div className="bar-label">{dim.label}</div>
                      <div className="track">
                        <div
                          className={`fill ${colorKey}`}
                          style={{ width: `${(dim.score / dim.max) * 100}%` }}
                        />
                      </div>
                      <div className="score-box">
                        <strong>{dim.score} / {dim.max}</strong>
                        <span className={`band-${colorKey}`}>{dim.bandLabel}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="two-col">
              {/* Strongest Areas — top 2 by score */}
              <div className="card">
                <h3>Strongest Areas</h3>
                <div className="list">
                  {strongestAreas.map((area) => (
                    <div className="item" key={area.title}>
                      <strong>{area.title}</strong>
                      <p>{area.body}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Priority Gaps — bottom 2 by score */}
              <div className="card">
                <h3>Priority Gaps</h3>
                <div className="list">
                  {priorityGaps.map((gap) => (
                    <div className="item" key={gap.title}>
                      <strong>{gap.title}</strong>
                      <p>{gap.body}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════════
            PAGE 4 — DEVELOPMENT PLAN
            Data source: bottom 3 dimensions by score
        ══════════════════════════════════════════ */}
        <section className="page">
          <div className="page-inner">
            <div className="topbar">
              <div>
                <div className="section-kicker">Page 4</div>
                <h2 className="section-title">Development Plan</h2>
              </div>
              <div className="small">90-day focus areas</div>
            </div>

            {/* Priority Actions table — bottom 3 dimensions sorted by score asc */}
            <div className="card" style={{ marginBottom: "var(--space-6)" }}>
              <h3>Priority Actions</h3>
              <table>
                <thead>
                  <tr>
                    <th>Focus area</th>
                    <th>Current score</th>
                    <th>Recommended intervention</th>
                    <th>Expected shift</th>
                  </tr>
                </thead>
                <tbody>
                  {priorityActions.map((row) => (
                    <tr key={row.focusArea}>
                      <td data-label="Focus Area">{row.focusArea}</td>
                      <td data-label="Current Score">{row.currentScore}</td>
                      <td data-label="Recommended Intervention">{row.intervention}</td>
                      <td data-label="Expected Shift">{row.expectedShift}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="two-col">
              <div className="card">
                <h3>How to Read This Report</h3>
                <p className="lead">
                  This report uses a clear sequence: a cover page, a concise
                  interpretation page, a score summary page, and action-oriented
                  follow-up pages. It is designed in a 4-page format for individual
                  use while keeping content practical and visually easy to present.
                </p>
              </div>
              <div className="card">
                <h3>Coaching Note</h3>
                <p className="lead">
                  The objective of this assessment is to identify which area needs
                  to be worked on and to focus the participant on improvement rather
                  than just where they stand today. This report is most useful when
                  reviewed alongside a manager or facilitator in a guided debrief.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════════
            BACK COVER
        ══════════════════════════════════════════ */}
        <section className="page back-cover">
          <div className="back-cover-gradient">
            {/* ── Header bar ── */}
            <div className="back-cover-header">
              <div style={{ fontSize:"0.72rem", textTransform:"uppercase", letterSpacing:".12em", fontWeight:700, color:"rgba(255,255,255,0.66)" }}>
                {/* Back Cover */}
              </div>
              <div style={{ fontSize:"0.72rem", color:"rgba(255,255,255,0.5)", letterSpacing:".05em" }}>
                Acumen Business Catalyst
              </div>
            </div>

            <div className="page-inner">
              <div style={{ display:"flex", flexDirection:"column", justifyContent:"center", flex:1 }}>
                <div className="closing" style={{ padding:"0.1rem" }}>
                  <h2>Moving Forward with Intentional Communication</h2>
                  <p>
                    Communication proficiency is not a fixed trait. It is a capability
                    that strengthens through awareness, disciplined practice, and
                    consistent feedback. Acumen Business Catalyst uses reports like
                    this to turn assessment data into focused development conversations.
                  </p>
                </div>
              </div>

              <div>
                <div className="card" style={{ marginTop:"1.5rem", background:"rgba(255,255,255,.06)", borderColor:"rgba(255,255,255,.14)", color:"white" }}>
                  <h3 style={{ color:"white" }}>Practical Communication Design</h3>
                  <p className="lead" style={{ color:"rgba(255,255,255,.84)" }}>
                    For this participant, the most effective development path starts with the
                    dimension scoring lowest on the profile — <strong style={{ color:"white" }}>{bottomDim}</strong> —
                    and applies the targeted intervention consistently over a 90-day period.
                    Pairing that focus with regular debrief conversations and peer feedback will
                    accelerate observable improvement and build lasting communication habits.
                  </p>
                </div>

                <div className="contact-grid">
                  <div className="contact"><span>Brand</span><strong>Acumen Business Catalyst</strong></div>
                  <div className="contact"><span>Focus</span><strong>Leadership • Culture • Capability</strong></div>
                  <div className="contact"><span>Report type</span><strong>Individual Assessment</strong></div>
                </div>

                <p className="light" style={{ marginTop:"1.5rem", fontSize:"0.75rem" }}>
                  This report is generated from the Communication Proficiency Questionnaire scoring framework.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>      
    </div>
    {/* ── Hidden offscreen report — mounts only while generating, unmounts after PDF saved ── */}
      {pdfData && (
        <HiddenCPQReport
          participantData={pdfData}
          onComplete={handlePdfComplete}
        />
      )}
    </>
  );
}